export interface MenuItem {
	label: string;
	hint?: string;
	active?: boolean;
	run: () => void;
}

export interface MenuGroup {
	label?: string;
	items: MenuItem[];
}

const GROUP = "px-3.5 pt-2 pb-1 text-xs font-bold uppercase tracking-wide text-ink-muted";
const ITEM =
	"flex w-full items-center gap-2.5 px-3.5 py-1.5 text-left hover:bg-surface-sunken focus:bg-surface-sunken focus:outline-none aria-[current=true]:font-bold";

// A layer anchored at a point of the viewport, closed by Escape, a press outside it or a resize. `onDismiss`
// runs only for Escape: a press outside hands the focus to whatever was pressed, and a close the caller makes
// to do something else leaves the focus to what it opens next. The returned close can be called again.
export function openFloating(host: HTMLElement, at: { left: number; top: number }, content: HTMLElement, onDismiss?: () => void): () => void {
	const layer = document.createElement("div");
	layer.className = "fixed z-50 min-w-56 max-w-sm overflow-auto rounded border border-line bg-surface-raised text-ink shadow-lg";
	layer.style.left = `${Math.max(8, Math.min(at.left, window.innerWidth - 320))}px`;
	layer.style.top = `${at.top}px`;
	// A long menu scrolls inside the space left below its anchor instead of running off the screen.
	layer.style.maxHeight = `${Math.max(200, window.innerHeight - at.top - 8)}px`;
	layer.append(content);
	host.append(layer);
	let open = true;
	const close = () => {
		if (!open) return;
		open = false;
		document.removeEventListener("pointerdown", onOutside, true);
		document.removeEventListener("keydown", onEscape, true);
		window.removeEventListener("resize", close);
		layer.remove();
	};
	const onOutside = (event: Event) => {
		if (layer.contains(event.target as Node)) return;
		close();
	};
	const onEscape = (event: KeyboardEvent) => {
		if (event.key !== "Escape") return;
		event.stopPropagation();
		close();
		onDismiss?.();
	};
	document.addEventListener("pointerdown", onOutside, true);
	document.addEventListener("keydown", onEscape, true);
	window.addEventListener("resize", close);
	return close;
}

export function belowOf(element: Element) {
	const rect = element.getBoundingClientRect();
	return { left: rect.left, top: rect.bottom + 4 };
}

export function menuElement(groups: MenuGroup[], close: () => void): HTMLElement {
	const list = document.createElement("div");
	list.setAttribute("role", "menu");
	list.className = "py-1.5";
	for (const group of groups) {
		if (group.label) {
			const heading = document.createElement("div");
			heading.className = GROUP;
			heading.textContent = group.label;
			list.append(heading);
		}
		for (const item of group.items) {
			const button = document.createElement("button");
			button.type = "button";
			button.setAttribute("role", "menuitem");
			button.className = ITEM;
			if (item.active) button.setAttribute("aria-current", "true");
			button.append(item.label);
			if (item.hint) {
				const hint = document.createElement("small");
				hint.className = "ml-auto text-ink-muted";
				hint.textContent = item.hint;
				button.append(hint);
			}
			button.addEventListener("click", () => {
				close();
				item.run();
			});
			list.append(button);
		}
	}
	list.addEventListener("keydown", (event) => {
		const buttons = [...list.querySelectorAll("button")];
		const at = buttons.indexOf(document.activeElement as HTMLButtonElement);
		const step = event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
		if (!step) return;
		event.preventDefault();
		buttons[(at + step + buttons.length) % buttons.length]?.focus();
	});
	return list;
}

export function openMenu(host: HTMLElement, at: { left: number; top: number }, groups: MenuGroup[], onDismiss?: () => void) {
	const content = document.createElement("div");
	const close = openFloating(host, at, content, onDismiss);
	content.append(menuElement(groups, close));
	content.querySelector("button")?.focus();
	return close;
}
